import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getDeviceStatus } from "../../services/deviceStatus";
import {
  cookingFromAlarms,
  resolveDisplayState,
  firmwareLedColorHex,
  formatTemp,
  PcoState,
  DisplayState,
  TempUnit,
} from "../../services/cookingState";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import LockIcon from "@mui/icons-material/Lock";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import FireplaceIcon from "@mui/icons-material/Fireplace";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import PowerIcon from "@mui/icons-material/Power";
import WifiIcon from "@mui/icons-material/Wifi";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type Props = {
  sensorName: string;
  isOnline: boolean;
  pcoState: PcoState;
  unit?: TempUnit;
};

// Fallback accent colors; cooking/cooling override with the firmware LED color.
const COLORS = {
  offline: "#7C4DFF", // deep purple
  off: "#B00020", // dark red
  lock: "#1976D2", // blue
  cookingFallback: "#F1C40F", // yellow
  ovenOn: "#FFC107", // amber
  coolingFallback: "#FB8C00", // orange
  on: "#388E3C", // green
  online: "#388E3C", // green (same as stove power enabled)
  unknown: "#607D8B", // blue-grey
};

function meta(
  state: DisplayState,
  tempC: number | null,
  unit: TempUnit
): { label: string; color: string; Icon: typeof WifiIcon } {
  const tempStr = tempC != null ? formatTemp(tempC, unit) : null;
  switch (state) {
    case "offline":
      return { label: "Sensor offline", color: COLORS.offline, Icon: WifiOffIcon };
    case "off":
      return {
        label: "Cooktop Disabled",
        color: COLORS.off,
        Icon: PowerOffIcon,
      };
    case "lock":
      return { label: "Cooktop locked", color: COLORS.lock, Icon: LockIcon };
    case "cooking":
      return {
        label: tempStr ? `Cooking · ${tempStr}` : "Cooking",
        color: tempC != null ? firmwareLedColorHex(tempC) : COLORS.cookingFallback,
        Icon: LocalFireDepartmentIcon,
      };
    case "ovenOn":
      return { label: "Oven On", color: COLORS.ovenOn, Icon: FireplaceIcon };
    case "cooling":
      return {
        label: tempStr ? `Cooling · ${tempStr}` : "Cooling",
        color: tempC != null ? firmwareLedColorHex(tempC) : COLORS.coolingFallback,
        Icon: AcUnitIcon,
      };
    case "on":
      return { label: "Cooktop Enabled", color: COLORS.on, Icon: PowerIcon };
    case "online":
      return { label: "Sensor online", color: COLORS.online, Icon: WifiIcon };
    default:
      return { label: "Unknown", color: COLORS.unknown, Icon: HelpOutlineIcon };
  }
}

export default function StoveStateBadge({
  sensorName,
  isOnline,
  pcoState,
  unit = "F",
}: Props) {
  // Only probe the device when it's reachable; offline short-circuits the
  // resolved state and needs no MQTT round-trip.
  const { data, isFetching } = useQuery({
    queryKey: ["device-status", sensorName],
    queryFn: () => getDeviceStatus(sensorName),
    enabled: isOnline,
    staleTime: 30_000,
    refetchInterval: isOnline ? 30_000 : false,
    retry: false,
  });

  const alarms = data?.alarms ?? null;
  const hottestTempC = data?.hottestTempC ?? null;
  const stovePoweredOn = data?.stovePoweredOn ?? null;
  const isCooking = isOnline ? cookingFromAlarms(alarms, pcoState) : null;

  const state = resolveDisplayState(
    isOnline,
    pcoState,
    isCooking,
    hottestTempC,
    stovePoweredOn
  );
  const { label, color, Icon } = meta(state, hottestTempC, unit);

  return (
    <span
      className="inline-flex items-center gap-1.5 font-medium text-sm whitespace-nowrap"
      title={
        isOnline && isFetching && !data
          ? "Checking live cooktop state…"
          : undefined
      }
    >
      <Icon fontSize="small" style={{ color }} />
      {label}
    </span>
  );
}
